import React, {
  useEffect,
  useState,
} from "react";
import {
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import BackButton from "../../../components/BackButton";
import TimeAndSeat from "./TimeAndSeat";
import Checkout from "./CheckoutSummary";
import "../../../styles/GetTicket.scss";
import { FaCalendar as FaCalendarRaw } from "react-icons/fa";
import { FaLocationDot as FaLocationDotRaw } from "react-icons/fa6";
import { IoPerson as IoPersonRaw } from "react-icons/io5";
import {
  tryFetch,
  IResponse,
} from "../../../helpers/FetchAPI";
import { getTokenOrDefault } from "../../../helpers/Storage";
import { useEventRating } from "../../hooks/useEventRating";

const toBoolean = (
  value: unknown
): boolean => {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "number") {
    return value === 1;
  }

  if (typeof value === "string") {
    const normalizedValue = value
      .trim()
      .toLowerCase();

    return (
      normalizedValue === "true" ||
      normalizedValue === "1"
    );
  }

  return false;
};

const GetTicketPage: React.FC = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const eventId =
    location.state?.eventId;

  const [eventData, setEventData] =
    useState<any>(null);

  const [selectedDate, setSelectedDate] =
    useState<string | null>(null);

  const [selectedTime, setSelectedTime] =
    useState<string | null>(null);

  const [
    selectedTicketType,
    setSelectedTicketType,
  ] = useState<string | null>(null);

  const [
    selectedSeatIds,
    setSelectedSeatIds,
  ] = useState<string[]>([]);

  const formattedRating = useEventRating(
    eventData?.event_rating || ""
  );

  useEffect(() => {
    let isMounted = true;

    const fetchEvent = async () => {
      try {
        if (!eventId) {
          console.error(
            "Event ID not found from navigation state."
          );

          if (isMounted) {
            setEventData(null);
          }

          return;
        }

        const res: IResponse =
          await tryFetch({
            url: `/events/${eventId}?timestamp=${Date.now()}`,
            method: "GET",
          });

        if (!isMounted) {
          return;
        }

        if (
          res.is_success &&
          res.data
        ) {
          const foundEvent =
            res.data as any;

          const normalizedIsSingleTicket =
            toBoolean(
              foundEvent.is_single_ticket
            );

          const normalizedIsSeatSection =
            toBoolean(
              foundEvent.is_seat_section
            );

          const mappedEvent = {
            id: foundEvent.id,

            event_title:
              foundEvent.event_title,

            venue: foundEvent.venue,

            event_rating:
              foundEvent.event_rating,

            description:
              foundEvent.description,

            organizer:
              foundEvent.organizer,

            dates: Array.isArray(
              foundEvent.all_dates
            )
              ? foundEvent.all_dates
              : [],

            is_seat_section:
              normalizedIsSeatSection,

            is_single_ticket:
              normalizedIsSingleTicket,

            tickets: Array.isArray(
              foundEvent.tickets
            )
              ? foundEvent.tickets.map(
                  (ticket: any) => {
                    const capacity =
                      Number(
                        ticket.capacity ??
                          0
                      );

                    const ticketCount =
                      Number(
                        ticket.ticket_count ??
                          0
                      );

                    const availableSlot =
                      Math.max(
                        capacity -
                          ticketCount,
                        0
                      );

                    return {
                      id: ticket.id,

                      ticket_type:
                        ticket.name,

                      price:
                        ticket.ticket_price,

                      available_slot:
                        String(
                          availableSlot
                        ),

                      capacity,

                      ticket_count:
                        ticketCount,
                    };
                  }
                )
              : [],

            house_rules:
              foundEvent.house_rules ||
              [],
          };

          setSelectedDate(null);
          setSelectedTime(null);
          setSelectedTicketType(null);
          setSelectedSeatIds([]);

          setEventData(mappedEvent);
        } else {
          console.error(
            "Failed to fetch event:",
            res.error
          );

          setEventData(null);
        }
      } catch (error) {
        console.error(
          "Error fetching event:",
          error
        );

        if (isMounted) {
          setEventData(null);
        }
      }
    };

    fetchEvent();

    return () => {
      isMounted = false;
    };
  }, [eventId, slug]);

  const formatEventDateRange = (
    dates: string[]
  ): string => {
    if (
      !Array.isArray(dates) ||
      dates.length === 0
    ) {
      return "No Dates";
    }

    const sortedDates = [...dates]
      .filter(
        (date) =>
          !Number.isNaN(
            new Date(date).getTime()
          )
      )
      .sort(
        (firstDate, secondDate) =>
          new Date(
            firstDate
          ).getTime() -
          new Date(
            secondDate
          ).getTime()
      );

    if (sortedDates.length === 0) {
      return "No Dates";
    }

    const formatDate = (
      date: string
    ) =>
      new Date(
        date
      ).toLocaleDateString(
        "en-US",
        {
          month: "long",
          day: "numeric",
          year: "numeric",
        }
      );

    const startDate = formatDate(
      sortedDates[0]
    );

    const endDate = formatDate(
      sortedDates[
        sortedDates.length - 1
      ]
    );

    return startDate === endDate
      ? startDate
      : `${startDate} - ${endDate}`;
  };

  const getFirstAvailableDate = (
    dates: string[]
  ): string | null => {
    if (!Array.isArray(dates)) {
      return null;
    }

    const sortedValidDates = [
      ...dates,
    ]
      .filter(
        (date) =>
          !Number.isNaN(
            new Date(date).getTime()
          )
      )
      .sort(
        (firstDate, secondDate) =>
          new Date(
            firstDate
          ).getTime() -
          new Date(
            secondDate
          ).getTime()
      );

    return (
      sortedValidDates[0] || null
    );
  };

  const handleProceed = async (
    ticketId: string,
    ticketCount: number,
    checkoutSelectedDate:
      | string
      | null,
    checkoutSelectedTime:
      | string
      | null
  ) => {
    const isSingleTicket = Boolean(
      eventData?.is_single_ticket
    );

    const firstAvailableDate =
      getFirstAvailableDate(
        eventData?.dates || []
      );

    const payloadSelectedDate =
      isSingleTicket
        ? firstAvailableDate
        : checkoutSelectedDate;

    if (
      !ticketId ||
      !payloadSelectedDate ||
      !checkoutSelectedTime
    ) {
      alert(
        "Please select ticket, date, and time first."
      );

      return;
    }

    const token =
      getTokenOrDefault();

    const payload = {
      ticket_type_id: ticketId,

      ticket_count:
        ticketCount.toString(),

      selected_dates: [
        payloadSelectedDate,
      ],

      seat_ids:
        eventData.is_seat_section
          ? selectedSeatIds
          : undefined,
    };

    try {
      const res: IResponse =
        await tryFetch({
          url: `/sec/events/${eventData.id}/tickets/${ticketId}`,
          method: "POST",
          token,
          payload,
        });

      if (res.is_success) {
        const createdTicketId:
          string =
          (res.data as any)?.id ||
          ticketId;

        navigate(
          `/preview/${createdTicketId}`
        );
      } else {
        alert(
          `Checkout failed: ${res.error}`
        );
      }
    } catch (error) {
      console.error(error);
      alert(
        "Something went wrong."
      );
    }
  };

  if (!eventData) {
    return <div>Loading...</div>;
  }

  const FaCalendar =
    FaCalendarRaw as React.ElementType;

  const FaLocationDot =
    FaLocationDotRaw as React.ElementType;

  const IoPerson =
    IoPersonRaw as React.ElementType;

  const isSeatSection = Boolean(
    eventData.is_seat_section
  );

  const isSingleTicket = Boolean(
    eventData.is_single_ticket
  );

  return (
    <div className="get-ticket">
      <BackButton />

      <h1 className="page-title">
        Get Tickets
      </h1>

      <hr className="divider" />

      <div className="event-details">
        <h2>
          {eventData.event_title}
        </h2>

        <p className="e-organizer">
          by {eventData.organizer}
        </p>

        <p>
          <FaCalendar className="icon" />

          {formatEventDateRange(
            eventData.dates
          )}
        </p>

        <p>
          <FaLocationDot className="icon" />

          {eventData.venue}
        </p>

        <p>
          <IoPerson className="icon" />

          {formattedRating}
        </p>
      </div>

      <TimeAndSeat
        key={`${eventData.id}-${String(
          isSingleTicket
        )}-${String(isSeatSection)}`}
        tickets={eventData.tickets}
        availableDates={
          eventData.dates
        }
        isSeatSection={
          isSeatSection
        }
        isSingleTicket={
          isSingleTicket
        }
        onDateSelect={
          setSelectedDate
        }
        onTimeSelect={
          setSelectedTime
        }
        onTicketSelect={
          setSelectedTicketType
        }
        onSeatSelect={
          setSelectedSeatIds
        }
      />

      <Checkout
        tickets={eventData.tickets}
        eventData={eventData}
        selectedDate={selectedDate}
        selectedTime={selectedTime}
        selectedTicketType={
          selectedTicketType
        }
        selectedSeatIds={
          selectedSeatIds
        }
        isSeatSection={
          isSeatSection
        }
        onProceed={handleProceed}
      />
    </div>
  );
};

export default GetTicketPage;